import React, { useEffect, useState, useCallback } from 'react';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ClipboardList, Edit, BarChart3, Download, Plus, Trash2, RefreshCw, Power } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * SurveyManager — BUS-6 (edit) + BUS-7 (results/download) for the Business plane.
 * Lists this tenant's surveys and lets Manager/Admin EDIT them (PUT /surveys/{id}),
 * VIEW aggregated results (GET /surveys/{id}/results) and DOWNLOAD them as CSV (client-side
 * from real backend data — no mock), and toggle active (PUT /surveys/{id}/toggle).
 * All calls are tenant/role-guarded server-side (403 surfaces honestly; no faked success).
 */
const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'text', label: 'Text Response' },
  { value: 'rating', label: 'Rating Scale' },
  { value: 'yes_no', label: 'Yes/No' },
];

const SurveyManager = () => {
  const { currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const businessId = currentBusiness?.id || currentBusiness?.brandId;
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // survey being edited
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);     // results payload being viewed
  const [resultsLoading, setResultsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/surveys/business/${businessId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`load ${res.status}`);
      const data = await res.json();
      setSurveys(Array.isArray(data?.surveys) ? data.surveys : []);
    } catch (e) {
      toast({ title: t('st.survey.loadFail'), description: t('st.survey.loadFailDesc'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [businessId, makeAuthenticatedRequest, toast]);

  useEffect(() => { load(); }, [load]);

  // ---------- Edit (BUS-6) ----------
  const openEdit = (s) => setEditing({
    ...s,
    questions: Array.isArray(s.questions) ? s.questions.map(q => ({ ...q })) : [],
    deadline: s.deadline ? String(s.deadline).slice(0, 10) : '',
  });

  const setField = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));
  const setQ = (idx, k, v) => setEditing(prev => ({
    ...prev, questions: prev.questions.map((q, i) => i === idx ? { ...q, [k]: v } : q),
  }));
  const setQOption = (qi, oi, v) => setEditing(prev => ({
    ...prev, questions: prev.questions.map((q, i) => i === qi ? { ...q, options: (q.options || []).map((o, j) => j === oi ? v : o) } : q),
  }));
  const addQOption = (qi) => setEditing(prev => ({
    ...prev, questions: prev.questions.map((q, i) => i === qi ? { ...q, options: [...(q.options || []), ''] } : q),
  }));
  const removeQuestion = (qi) => setEditing(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== qi) }));
  const addQuestion = () => setEditing(prev => ({
    ...prev, questions: [...prev.questions, { id: Date.now().toString(), type: 'text', question: '', options: [], required: true }],
  }));

  const saveEdit = async () => {
    if (!editing?.title || (editing.questions || []).length === 0) {
      toast({ title: t('st.survey.missingInfo'), description: t('st.survey.missingInfoDesc'), variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: editing.title,
        description: editing.description || '',
        survey_type: editing.survey_type || 'unpaid',
        reward: editing.reward || '',
        duration: editing.duration || '',
        difficulty: editing.difficulty || 'Medium',
        questions: editing.questions.map(q => ({
          id: q.id || Date.now().toString(),
          type: q.type || 'text',
          question: q.question || '',
          options: q.type === 'multiple_choice' ? (q.options || []).filter(o => String(o).trim()) : (q.options || []),
          required: q.required !== false,
        })),
      };
      if (editing.deadline) body.deadline = new Date(editing.deadline).toISOString();
      const res = await makeAuthenticatedRequest(`/api/v1/surveys/${editing.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 403) { toast({ title: t('st.survey.notAllowed'), description: t('st.survey.notAllowedEdit'), variant: 'destructive' }); return; }
      if (!res.ok) throw new Error(`save ${res.status}`);
      toast({ title: t('st.survey.updated'), description: t('st.survey.updatedDesc') });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: t('st.survey.updateFail'), description: t('st.survey.updateFailDesc'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/surveys/${s.id}/toggle`, { method: 'PUT', credentials: 'include' });
      if (res.status === 403) { toast({ title: t('st.survey.notAllowed'), description: t('st.survey.notAllowedStatus'), variant: 'destructive' }); return; }
      if (!res.ok) throw new Error(`toggle ${res.status}`);
      load();
    } catch (e) {
      toast({ title: t('st.survey.statusFail'), variant: 'destructive' });
    }
  };

  // ---------- Results + CSV (BUS-7) ----------
  const viewResults = async (s) => {
    setResultsLoading(true); setResults({ survey_id: s.id, title: s.title, loading: true });
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/surveys/${s.id}/results`, { credentials: 'include' });
      if (res.status === 403) { setResults(null); toast({ title: t('st.survey.notAllowed'), description: t('st.survey.notAllowedResults'), variant: 'destructive' }); return; }
      if (!res.ok) throw new Error(`results ${res.status}`);
      setResults(await res.json());
    } catch (e) {
      setResults(null);
      toast({ title: t('st.survey.resultsFail'), variant: 'destructive' });
    } finally {
      setResultsLoading(false);
    }
  };

  const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const downloadCsv = () => {
    if (!results || !Array.isArray(results.questions)) return;
    const rows = [['Question', 'Type', 'Responses', 'Answer', 'Count/Value']];
    results.questions.forEach(q => {
      if (q.tally && Object.keys(q.tally).length) {
        Object.entries(q.tally).forEach(([ans, cnt]) => rows.push([q.question, q.type, q.count, ans, cnt]));
      } else if (q.average !== undefined && q.average !== null) {
        rows.push([q.question, q.type, q.count, 'average', q.average]);
      } else if (Array.isArray(q.responses)) {
        q.responses.forEach(r => rows.push([q.question, q.type, q.count, r, '']));
      } else {
        rows.push([q.question, q.type, q.count, '', '']);
      }
    });
    const csv = rows.map(r => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `survey_results_${results.survey_id}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg mt-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <ClipboardList className="h-5 w-5" />
            <span>{t('st.survey.manage', { count: surveys.length })}</span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> {t('common.refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">{t('st.survey.loading')}</p>
        ) : surveys.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">{t('st.survey.empty')}</p>
        ) : (
          <div className="space-y-3">
            {surveys.map((s) => (
              <div key={s.id} className="p-4 border rounded-lg bg-white/60 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium truncate">{s.title}</h4>
                    <Badge className={s.active === false ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}>
                      {s.active === false ? t('st.inactive') : t('st.active')}
                    </Badge>
                    <Badge variant="outline">{t('st.survey.qCount', { count: (s.questions || []).length })}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{s.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('st.survey.responses', { count: s.participants || 0 })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(s)}><Edit className="h-4 w-4 mr-1" />{t('common.edit')}</Button>
                  <Button variant="outline" size="sm" onClick={() => viewResults(s)}><BarChart3 className="h-4 w-4 mr-1" />{t('st.survey.results')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(s)} title={t('st.survey.toggleActive')}><Power className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit dialog (BUS-6) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('st.survey.editTitle')}</DialogTitle>
            <DialogDescription>{t('st.survey.editDesc')}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>{t('st.survey.fTitle')}</Label>
                <Input value={editing.title || ''} onChange={(e) => setField('title', e.target.value)} />
              </div>
              <div>
                <Label>{t('st.survey.fDesc')}</Label>
                <Textarea value={editing.description || ''} onChange={(e) => setField('description', e.target.value)} />
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>{t('st.survey.fType')}</Label>
                  <Select value={editing.survey_type || 'unpaid'} onValueChange={(v) => setField('survey_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">{t('st.survey.paid')}</SelectItem>
                      <SelectItem value="unpaid">{t('st.survey.unpaid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('st.survey.fReward')}</Label>
                  <Input value={editing.reward || ''} onChange={(e) => setField('reward', e.target.value)} />
                </div>
                <div>
                  <Label>{t('st.survey.fDeadline')}</Label>
                  <Input type="date" value={editing.deadline || ''} onChange={(e) => setField('deadline', e.target.value)} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t('st.survey.questions', { count: (editing.questions || []).length })}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addQuestion}><Plus className="h-4 w-4 mr-1" />{t('st.survey.addQuestion')}</Button>
                </div>
                {(editing.questions || []).map((q, qi) => (
                  <div key={q.id || qi} className="p-3 border rounded-md bg-white/60 space-y-2">
                    <div className="flex items-center gap-2">
                      <Select value={q.type || 'text'} onValueChange={(v) => setQ(qi, 'type', v)}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {QUESTION_TYPES.map(qt => <SelectItem key={qt.value} value={qt.value}>{t('st.survey.qtype.' + qt.value)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" className="text-red-600 ml-auto" onClick={() => removeQuestion(qi)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <Textarea placeholder={t('st.survey.questionPh')} value={q.question || ''} onChange={(e) => setQ(qi, 'question', e.target.value)} />
                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2">
                        {(q.options || []).map((o, oi) => (
                          <Input key={oi} placeholder={t('st.survey.optionPh', { n: oi + 1 })} value={o} onChange={(e) => setQOption(qi, oi, e.target.value)} />
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => addQOption(qi)}><Plus className="h-4 w-4 mr-1" />{t('st.survey.addOption')}</Button>
                      </div>
                    )}
                    <label className="flex items-center space-x-2 text-sm">
                      <input type="checkbox" checked={q.required !== false} onChange={(e) => setQ(qi, 'required', e.target.checked)} />
                      <span>{t('st.survey.required')}</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
                <Button onClick={saveEdit} disabled={saving}>{saving ? t('st.savingEllipsis') : t('st.saveChanges')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Results dialog (BUS-7) */}
      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('st.survey.resultsTitle')}</DialogTitle>
            <DialogDescription>{results?.title}</DialogDescription>
          </DialogHeader>
          {resultsLoading ? (
            <p className="text-sm text-gray-500 py-6 text-center">{t('st.survey.loadingResults')}</p>
          ) : results && Array.isArray(results.questions) ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('st.survey.totalResponses', { count: results.total_responses })}</span>
                <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!results.total_responses}>
                  <Download className="h-4 w-4 mr-1" />{t('st.survey.downloadCsv')}
                </Button>
              </div>
              {results.questions.map((q, i) => (
                <div key={q.question_id || i} className="p-3 border rounded-md bg-white/60">
                  <p className="font-medium text-sm">{i + 1}. {q.question} <span className="text-gray-400">({q.type}, {q.count})</span></p>
                  {q.tally && Object.keys(q.tally).length > 0 && (
                    <ul className="mt-2 text-sm text-gray-700 space-y-1">
                      {Object.entries(q.tally).map(([ans, cnt]) => (
                        <li key={ans} className="flex justify-between"><span>{ans}</span><span className="font-medium">{cnt}</span></li>
                      ))}
                    </ul>
                  )}
                  {q.average !== undefined && q.average !== null && (
                    <p className="mt-2 text-sm">{t('st.survey.average')} <span className="font-medium">{q.average}</span></p>
                  )}
                  {Array.isArray(q.responses) && q.responses.length > 0 && (
                    <ul className="mt-2 text-sm text-gray-700 list-disc pl-5 space-y-1">
                      {q.responses.slice(0, 50).map((r, j) => <li key={j}>{r}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-6 text-center">{t('st.survey.noResults')}</p>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SurveyManager;
